import {
  type LDClient,
  type LDContext,
  type LDOptions,
  initialize
} from "launchdarkly-react-client-sdk";

// SN-20181: on about 1.5% of page loads the React flag set came up nearly empty
// (Momentum recorded 7 flag values instead of ~300), so every flag-gated control
// vanished until a reload. When `withLDProvider` creates the client itself, its
// state starts at `{}` and has two ways to stay there:
//   - on a `failed` initialisation it keeps `{}`, though the client may hold flags
//     (it emits `failed` and then `ready`, so later reads work but React never asks);
//   - it subscribes to `change` only after awaiting initialisation, so a flag update
//     that lands in between (the localStorage bootstrap signals ready on the next
//     tick while its network refresh is still in flight) is never delivered.
// After that, React only receives *later* changes: a diff against a flag set it
// never had.
//
// Handing the provider a client that is already ready closes both: it then reads
// `ldClient.allFlags()` and subscribes in the same synchronous step, so React starts
// from whatever the client holds and every later change is a diff against that.

/**
 * Creates the app's LaunchDarkly client and resolves with it once the client has
 * settled. `waitUntilReady()` resolves on success AND on failure, so a failed
 * initialisation still reaches the provider (with whatever flags the client holds)
 * instead of leaving it on an empty set.
 */
export function createLaunchDarklyClient(
  clientSideID: string,
  context: LDContext,
  options: LDOptions
): Promise<LDClient> {
  const client = initialize(clientSideID, context, {
    // What `withLDProvider` sets when it creates the client, kept so the event
    // volume and the SDK's reporting are unchanged: without this, `allFlags()`
    // would record an evaluation for every flag in the project.
    wrapperName: "react-client-sdk",
    sendEventsOnlyForVariation: true,
    ...options
  });
  return client.waitUntilReady().then(() => client);
}
